<template>
    <div>
        <div :class="[isOpen ? 'selected' : '', 'flex items-center justify-between']">
            <div class="flex items-center flex-grow-1" @click.prevent="openSavedReply()">
                <button class="btn collapsed flex items-center font-normal text-sm text-left sm:font-medium sm:text-base capitalize" @click.prevent="">
                    <i :class="replyTypeIconClass" class="mr-1"></i> {{ form.name }}
                </button>
            </div>

            <div class="flex items-center justify-end">
                <button class="text-gray-400 transition duration-300 hover:text-yellow-600 active:text-yellow-800 px-2 flex items-center" v-if="!isOpen"
                        @click.prevent="beforeArchive()">

                    <span class="px-1">
                        <icon-archive-box v-if="!savedReply.archived_at" class="w-6 h-6"></icon-archive-box>
                        <icon-refresh v-else class="w-6 h-6"></icon-refresh>
                    </span>

                    <span>{{ savedReply.archived_at ? "Unarchive" : "Archive" }}</span>
                </button>

                <span v-show="!isOpen" class="m-2 font-medium px-2 py-1 rounded-md cursor-default" :class="replyCategory">
                    {{ form.category | titlecase }}
                </span>
            </div>
        </div>

        <div :class="[isOpen ? 'show' : '', 'collapse']">
            <div class="card-body">
                <!--Fields-->
                <div class="form-group row pb-2">
                    <div class="col-md-5">
                        <label style="display:inherit">Category</label>

                        <el-select class="col-md-12 saved-reply-top-row" style="padding:0"
                                   id="category"
                                   v-model="form.category"
                                   :disabled="hasCommunicationScenarios"
                                   :title="hasCommunicationScenarios? 'You cannot change the category on a saved reply that is linked to communication scenario': ''"
                                   placeholder="Select Category">

                            <el-option v-for="category in categories" :value="category" :key="category" :label="category | titlecase"></el-option>
                        </el-select>

                        <span class="invalid-feedback" :class="{'d-block': form.errors.has('category')}"
                              v-show="form.errors.has('category')">
                            {{ form.errors.get("category") }}
                        </span>
                    </div>

                    <div class="col-md-2">
                        <label style="display:inherit">Type</label>

                        <el-select class="col-md-12 saved-reply-top-row" style="padding:0" id="type" v-model="form.type"
                                   placeholder="Select type" :disabled="hasCommunicationScenarios"
                                   :title="hasCommunicationScenarios? 'You cannot change the type on a saved reply that is linked to communication scenario': ''">
                            <el-option value="email" label="Email"></el-option>
                            <el-option value="sms" label="SMS"></el-option>
                        </el-select>

                        <span
                            v-show="form.errors.has('type')">
                            {{ form.errors.get("type") }}
                        </span>
                    </div>

                    <div class="col-md-5">
                        <label style="display:inherit">Audience</label>

                        <el-select class="col-md-12 saved-reply-top-row" style="padding:0" id="audience" v-model="form.audience"
                                   :class="{'is-invalid': form.errors.has('audience')}"
                                   multiple clearable
                                   placeholder="Select audience">
                            <el-option v-for="role in roles" :key="role" :value="role" :label="role | titlecase"></el-option>
                        </el-select>

                        <span class="invalid-feedback" v-show="form.errors.has('audience')">
                            {{ form.errors.get("audience") }}
                        </span>
                    </div>
                </div>

                <div class="form-group row pb-2">
                    <div class="col-md-5">
                        <label>Reply Name</label>
                        <input
                            v-model="form.name"
                            class="form-control"
                            type="text"
                            id="name"
                            name="name"
                            :class="{'is-invalid': form.errors.has('name')}">
                        <span class="invalid-feedback" v-show="form.errors.has('name')">
                            {{ form.errors.get("name") }}
                        </span>
                    </div>

                    <div class="col-md-7" v-show="form.type !== 'sms'">
                        <label>Subject<span class="small relative"> (Optional)</span>
                            <i class="mdi mdi-information absolute ml-1" data-toggle="tooltip"
                               title="When provided the subject will be added to the default asset name / ticket number where you are sending the email/sms from"></i>
                        </label>

                        <input v-model="form.subject"
                               class="form-control"
                               type="text"
                               placeholder="Optional"
                               id="subject"
                               name="subject"
                               :class="{'is-invalid': form.errors.has('subject')}">

                        <span class="invalid-feedback" v-show="form.errors.has('subject')">
                            {{ form.errors.get("subject") }}
                        </span>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-12">
                        <!--body heading / count / tag select-->
                        <div class="flex flex-row justify-between pb-3">
                            <div class="flex flex-row">
                                <label class="mb-0 flex items-end">Body</label>
                                <p v-if="form.type === 'sms'" class="flex items-end text-sm px-2"
                                   :class="smsCharsRemaining < 0 ? 'text-red-600' : 'text-gray-400'">
                                    {{ smsCharsRemaining }}
                                </p>
                            </div>

                            <div class="custom-drop-group">
                                <el-select v-model="selectedTag"
                                           :filterable=true
                                           placeholder="Select Tag To Insert"
                                           @change="insertAtCaret"
                                           :disabled="smsCharsRemaining <= 0 && form.type === 'sms'">

                                    <el-option-group
                                        v-for="group in tagGroups"
                                        :key="group.label"
                                        :label="group.label"
                                        class="custom-drop-group-header">
                                        <el-option
                                            v-for="item in group.options"
                                            :key="item.value"
                                            class="custom-group-item"
                                            :label="item.label"
                                            :value="item.value">
                                        </el-option>
                                    </el-option-group>
                                </el-select>
                            </div>
                        </div>

                        <textarea ref="savedReplyBodyField"
                                  v-model="form.body"
                                  class="form-control"
                                  :maxlength="form.type === 'sms' ? smsMaxLength : 524288"
                                  type="text"
                                  :rows="form.type === 'sms' ? 3 : 17"
                                  id="body"
                                  name="body"
                                  :class="{'is-invalid': form.errors.has('body')}">
                        </textarea>

                        <span class="invalid-feedback" v-show="form.errors.has('body')">
                            {{ form.errors.get("body") }}
                        </span>
                    </div>
                </div>

                <!--Buttons-->
                <div style="display: block !important; padding:8px 0 30px 0" class="flex">
                    <div class="pull-left">
                        <button class="btn btn-sm waves-effect waves-light ml-1 button-white"
                                @click.prevent="beforeArchive()">
                            {{ savedReply.archived_at ? "Unarchive" : "Archive" }}
                        </button>
                    </div>

                    <button class="btn btn-sm btn-success waves-effect waves-light pull-right"
                            @click.prevent="updateSavedReply()">
                        Update
                    </button>
                </div>
            </div>
        </div>

        <archive-and-unarchive-modal :show="showArchivingModal" :model-id="savedReply.id" model-type="saved reply" url-override="savedreplies" :archiving="isArchiving"
                                     :additional-text="additionalArchivingText"
                                     :additional-text-class="hasCommunicationScenarios ? 'text-yellow-600 bg-yellow-100 p-1 px-2 flex rounded' : ''"
                                     @actioned="afterArchiving" v-if="showArchivingModal" @close="toggleArchivingModal">
        </archive-and-unarchive-modal>
    </div>
</template>

<script>
import TagGroupsMixin from "../../mixins/tag-groups-mixin";
import ArchivingMixin from "../../mixins/archiving-mixin";

export default {
    name: "SavedReply",

    mixins: [ArchivingMixin, TagGroupsMixin],

    props: ["savedReply", "roles", "categories"],

    data () {
        return {
            isOpen: false,
            form: new AppForm({
                category: this.savedReply.category,
                type: this.savedReply.type,
                audience: this.savedReply.audience,
                id: this.savedReply.id,
                user_id: this.savedReply.user_id,
                name: this.savedReply.name,
                subject: this.savedReply.subject,
                body: this.savedReply.body,
            }),
            additionalArchivingText: "",
            showCharCount: false,
            smsMaxLength: 350
        };
    },

    watch: {
        "form.type" (newValue) {
            if (newValue === "sms" && this.form.body.length > this.smsMaxLength) {
                this.form.body = this.form.body.slice(0, this.smsMaxLength);
                this.showCharCount = true;
            } else {
                this.showCharCount = false;
            }
        },

        //tags insert into the form.body object and skip the max length on the input
        "form.body" (newValue) {
            if (this.form.type === "sms" && newValue.length > 350) {
                this.form.body = this.form.body.slice(0, this.smsMaxLength);
            }
        }
    },

    created () {
        let self = this;

        Bus.$on("open-saved-reply", function (id) {
            if (self.savedReply.id !== id) {
                self.isOpen = false;
                $("#" + self.form.id).removeClass("selected");
            }
        });
    },

    computed: {
        smsCharsRemaining () {
            return this.form?.type === "sms" ? this.smsMaxLength - this.form.body.length : this.smsMaxLength;
        },

        replyTypeIconClass: function () {
            return this.form.type === "email" ? "mdi mdi-email" : "mdi mdi-cellphone-android";
        },

        replyCategory: function () {
            switch (this.form.category) {
                case "maintenance": {
                    return "bg-green-100 text-green-600";
                }
                case "maintenance requests": {
                    return "bg-blue-100 text-blue-600";
                }
                case "quotes & invoices": {
                    return "bg-yellow-100 text-yellow-600";
                }
                case "inspections": {
                    return "bg-indigo-100 text-indigo-600";
                }
                default: {
                    return "bg-gray-100 text-gray-600";
                }
            }
        },

        tagGroups () {
            if (this.form.category === "maintenance") {
                return this.tagGroupAlways.concat(this.tagGroupProperty).concat(this.tagGroupMaintenance);
            } else if (this.form.category === "inspections") {
                return this.tagGroupAlways.concat(this.tagGroupProperty).concat(this.tagGroupInspections);
            } else if (this.form.category === "maintenance requests") {
                return this.tagGroupAlways.concat(this.tagGroupMaintenanceRequests);
            } else if (this.form.category === "quotes & Invoices") {
                return this.tagGroupAlways.concat(this.tagGroupProperty).concat(this.tagGroupMaintenance);
            } else {
                return this.tagGroupAlways;
            }
        },

        hasCommunicationScenarios () {
            return this.savedReply.has_communication_scenarios;
        },
    },

    methods: {
        toggleAccordion: function () {
            this.isOpen = !this.isOpen;
        },

        openSavedReply () {
            this.toggleAccordion();
            Bus.$emit("open-saved-reply", this.form.id);
        },

        updateSavedReply () {
            if (this.form.type === "sms") {
                this.form.subject = "";
            }
            App.patch("/savedreplies/" + this.savedReply.id + "/update", this.form)
            .then(() => {
                window.toastr.success("Saved Reply Updated");
            })
            .catch((error) => {
                window.toastr.error("Could not update saved reply. " + error.message);
            });
        },

        archiveSavedReply () {
            axios.patch("/savedreplies/" + this.savedReply.id + "/archive")
            .then(() => {
                window.toastr.success("Saved Reply Archived");

            })
            .catch(() => {
                window.toastr.error("Could not archive saved reply");
            });
        },

        unarchiveSavedReply () {
            axios.patch("/savedreplies/" + this.savedReply.id + "/unarchive")
            .then(() => {
                window.toastr.success("Saved Reply Unarchived");

            })
            .catch(() => {
                window.toastr.error("Could not unarchive saved reply");
            });
        },

        // only show the confirmation if the saved reply is linked to a communication scenario
        beforeArchive () {
            this.additionalArchivingText = "Archived saved replies won't show in any lists. ";
            if (this.hasCommunicationScenarios) {
                this.additionalArchivingText += "This saved reply is linked to at least one communication scenario. Archiving this will UNLINK it from all scenarios.";
            } else {
                this.additionalArchivingText += "";
            }

            this.archiveOrUnarchiveModel(!this.savedReply.archived_at);
        },

        afterArchiving (archiving) {
            if (archiving) {
                this.savedReply.archived_at = this.$dayjs();
            } else {
                this.savedReply.archived_at = null;
            }

            this.$emit("saved-reply-archived-or-unarchived", this.savedReply.id);
        }
    },
};
</script>

<style>
.saved-reply-top-row .el-input__inner {
    min-height: 2.5rem;
}
</style>
